import { useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Check, User } from "lucide-react";
import { getAuthSessionSnapshot, subscribeAuthChanges } from "@envirohub/utils";
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@envirohub/ui";
import { useAuthContexts, type AuthContext } from "@/api";
import { contextKey, customerContexts, isSameContext } from "@/lib/contexts";

function useAuthSession() {
  return useSyncExternalStore(subscribeAuthChanges, getAuthSessionSnapshot, () => null);
}

interface ContextSwitcherMenuItemsProps {
  /** Owned by the always-mounted layout: the menu unmounts as soon as an item is picked. */
  onSwitch: (context: AuthContext) => void;
  switching: boolean;
}

/**
 * "Switch account" group for the profile menu. Renders nothing unless the user
 * has more than one customer context (personal account and/or companies).
 */
export function ContextSwitcherMenuItems({ onSwitch, switching }: ContextSwitcherMenuItemsProps) {
  const { t } = useTranslation();
  const session = useAuthSession();
  // Mounted only while the menu is open, so this fetches on open.
  const contextsQuery = useAuthContexts(Boolean(session));
  const active = session?.activeContext ?? null;
  const contexts = customerContexts(
    (contextsQuery.data ?? session?.availableContexts ?? []) as AuthContext[],
  );

  if (!active || contexts.length < 2) return null;

  return (
    <>
      <DropdownMenuLabel className="text-xs font-semibold text-surface-on-variant">
        {t("nav.switchAccount")}
      </DropdownMenuLabel>
      {contexts.map((context) => {
        const current = isSameContext(context, active);
        const isCompany = context.scope === "CUSTOMER_B2B";
        const Icon = isCompany ? Building2 : User;
        return (
          <DropdownMenuItem
            key={contextKey(context)}
            disabled={switching}
            aria-current={current ? "true" : undefined}
            onClick={() => {
              if (!current) onSwitch(context);
            }}
            className="cursor-pointer gap-2"
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block truncate">
                {isCompany ? (context.organizationName ?? context.label) : context.label}
              </span>
              <span className="block text-xs text-surface-on-variant">
                {t(`profile.account.scopes.${context.scope}`)}
              </span>
            </span>
            {current ? <Check className="h-4 w-4 shrink-0 text-primary" /> : null}
          </DropdownMenuItem>
        );
      })}
      <DropdownMenuSeparator />
    </>
  );
}
